import { useRef, useEffect, useCallback } from "react";
import { Chat } from "@launch10/use-langgraph-stream";
import { useBrainstormSelector } from "@components/brainstorm/hooks";
import { CreditGate } from "@components/shared/chat/input/CreditGate";
import { setTextareaRef } from "@lib/brainstormTextarea";
import { useIsOutOfCredits, useCreditStore } from "~/stores/creditStore";
import { StopIcon } from "@heroicons/react/24/outline";
import { ArrowRight, Paperclip } from "lucide-react";

const DEFAULT_PLACEHOLDER =
  'e.g. "FreshFund is a budgeting tool that helps freelancers track income and expenses."';

interface BrainstormInputProps {
  /** Maximum number of rows before the textarea scrolls internally. */
  maxRows?: number;
}

/**
 * Brainstorm chat input using Chat compound components from use-langgraph-stream.
 *
 * The only brainstorm-specific pieces here are:
 * - placeholderText: dynamic placeholder from backend
 * - setTextareaRef: for external focus management (e.g., suggestion clicks)
 */
export function BrainstormInput({ maxRows }: BrainstormInputProps) {
  const placeholderText = useBrainstormSelector((s) => s.values?.placeholderText);
  const isOutOfCredits = useIsOutOfCredits();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setTextareaRef(textareaRef);
  }, []);

  const handleOutOfCreditsClick = useCallback(() => {
    if (isOutOfCredits) {
      // Force-show: explicit user action should always show the modal,
      // even if it was recently dismissed.
      useCreditStore.setState({ showOutOfCreditsModal: true });
    }
  }, [isOutOfCredits]);

  return (
    <div className="px-4 pb-8">
      <CreditGate className="max-w-3xl mx-auto">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div onClick={handleOutOfCreditsClick}>
          <Chat.Input.DropZone className="relative bg-white border border-neutral-300 rounded-xl shadow-(--shadow-chat-default) hover:shadow-(--shadow-chat-delight) focus-within:shadow-(--shadow-chat-delight) transition-shadow p-4 flex flex-col min-h-[120px]">
            <Chat.Input.AttachmentList className="flex flex-wrap gap-2 mb-3" />

            <Chat.Input.Textarea
              ref={textareaRef}
              placeholder={placeholderText || DEFAULT_PLACEHOLDER}
              className=""
              style={{ color: "#74767a" }}
              maxRows={maxRows}
            />

            <div className="flex items-center justify-between mt-auto pt-2">
              <Chat.Input.FileButton className="text-base-500">
                <Paperclip className="w-5 h-5" strokeWidth={1.5} />
              </Chat.Input.FileButton>

              <Chat.Input.SubmitButton
                stopIcon={<StopIcon className="w-4 h-4" strokeWidth={2} />}
                className="rounded-xl bg-primary-500 text-white hover:bg-primary-600 size-9 flex items-center justify-center"
              >
                <ArrowRight className="size-4" />
              </Chat.Input.SubmitButton>
            </div>
          </Chat.Input.DropZone>
        </div>
      </CreditGate>
    </div>
  );
}
